import { ConfigProvider, type ThemeConfig } from 'antd';
import { type FC, type PropsWithChildren, useMemo } from 'react';
import { darkTheme } from '../tokens/dark';
import { lightTheme } from '../tokens/light';
import type { ThemeMode } from '../types';
import { mergeTheme } from '../utils/merge';
import { useOptionalThemeMode } from './useThemeMode';

export type { ThemeMode };

/** The brand token set for a mode, straight from the Theme Buddy export. */
export const brandTheme = (mode: ThemeMode = 'light'): ThemeConfig => (mode === 'dark' ? darkTheme : lightTheme);

export interface EnmonThemeProviderProps extends PropsWithChildren {
  /**
   * Force a mode. Omit it to follow the surrounding `ThemeModeProvider`, and fall back to `light`
   * when there is none.
   */
  mode?: ThemeMode;
  /**
   * An app's existing theme to layer the brand tokens on top of. Brand always wins —
   * see {@link mergeTheme}.
   */
  base?: ThemeConfig;
  /**
   * Components the brand config should keep its hands off entirely, styled by `base` alone. For a
   * component an app still renders through its own wrapper, so the brand bag has nothing to say
   * about it yet. Pass a stable reference — it is a dependency of the merge.
   */
  omitBrandComponents?: readonly string[];
}

/**
 * `ConfigProvider` preconfigured with the Enmon brand tokens.
 * `hashed: false` matches what all three apps already do; pass it through `base` to change it.
 */
export const EnmonThemeProvider: FC<EnmonThemeProviderProps> = ({ children, mode, base, omitBrandComponents }) => {
  const themeMode = useOptionalThemeMode();
  const resolved = mode ?? themeMode?.mode ?? 'light';

  const theme = useMemo(() => {
    const brand = brandTheme(resolved);
    const components = { ...brand.components };

    for (const name of omitBrandComponents ?? []) {
      delete components[name as keyof typeof components];
    }

    return mergeTheme({ ...brand, components }, { hashed: false, ...base });
  }, [base, resolved, omitBrandComponents]);

  return <ConfigProvider theme={theme}>{children}</ConfigProvider>;
};
